/**
 * RiskHeatGrid — the 5 × 5 likelihood × impact grid, as props (MYD-502).
 *
 * Pure. HeatMap feeds it on the Risks tab, where a cell is a button that
 * opens the risks behind it; a presentation slide feeds it from a resolved
 * payload, where cells are plain and a tap turns the page. `size="slide"`
 * scales the type for the 1280x720 stage; the default is the tab's.
 */

import { cn } from 'lib/utils'

import { IMPACT_LABELS, LIKELIHOOD_LABELS, SEVERITY_CLASSES, severityFromScore } from './riskScale'

export interface GridCell {
	likelihood: number
	impact: number
}

interface RiskHeatGridProps {
	/** Risk count per cell, keyed `${likelihood}-${impact}`. */
	counts: Map<string, number>
	/** Omit for a static grid (a slide): cells are then not buttons. */
	onSelectCell?: (cell: GridCell) => void
	selectedCell?: GridCell | null
	size?: 'card' | 'slide'
}

/** Count per cell from a list of risks; anything off the 1–5 scale is skipped. */
export const countByCell = (risks: Array<{ likelihood: unknown; impact: unknown } | null | undefined>) => {
	const counts = new Map<string, number>()
	for (const risk of risks) {
		// A slide's payload comes off the network; a hole in it must not throw.
		const L = Number(risk?.likelihood)
		const I = Number(risk?.impact)
		if (!(L >= 1 && L <= 5 && I >= 1 && I <= 5)) continue
		const key = `${L}-${I}`
		counts.set(key, (counts.get(key) ?? 0) + 1)
	}
	return counts
}

const RiskHeatGrid = ({ counts, onSelectCell, selectedCell = null, size = 'card' }: RiskHeatGridProps) => {
	const slide = size === 'slide'

	return (
		<div className="flex items-stretch gap-2">
			<div
				className={cn('flex items-center justify-center text-gray-500 uppercase tracking-wider shrink-0', slide ? 'text-[14px]' : 'text-[10px]')}
				style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
				aria-hidden
			>
				Impact →
			</div>
			{/* Grid + axis */}
			<div className="flex-1 min-w-0">
				{/* Rows: impact 5 at top, impact 1 at bottom */}
				<div className="grid gap-1" style={{ gridTemplateRows: 'repeat(5, minmax(0, 1fr))' }}>
					{[5, 4, 3, 2, 1].map(impactRow => (
						<div key={impactRow} className="flex items-stretch gap-1">
							<div className={cn('flex items-center justify-end text-gray-500 pr-1', slide ? 'w-36 text-[15px]' : 'w-16 text-[10px]')}>
								<span className="text-gray-700 font-semibold mr-1">{impactRow}</span>
								{IMPACT_LABELS[impactRow]}
							</div>
							{[1, 2, 3, 4, 5].map(likelihoodCol => {
								const score = likelihoodCol * impactRow
								const sev = severityFromScore(score)
								const key = `${likelihoodCol}-${impactRow}`
								const count = counts.get(key) ?? 0
								const isSelected = selectedCell?.likelihood === likelihoodCol && selectedCell?.impact === impactRow
								const className = cn(
									'flex-1 rounded-md border flex flex-col items-center justify-center transition-colors font-semibold',
									slide ? 'min-h-[64px] text-[22px]' : 'aspect-square min-h-[44px] text-sm focus:outline-none focus:ring-2 focus:ring-[#0080fe]/40',
									SEVERITY_CLASSES[sev].cell,
									isSelected && 'ring-2 ring-[#0080fe]',
									count === 0 && 'opacity-60',
								)
								const content = (
									<>
										<span className={slide ? 'text-[24px]' : 'text-base'}>{count}</span>
										{/* Size BEFORE leading-none: tailwind-merge drops a line-height
										    that precedes a font-size class, and the digits then sit lower. */}
										<span className={cn(slide ? 'text-[13px]' : 'text-[9px]', 'font-normal opacity-70 leading-none')}>{score}</span>
									</>
								)
								const label = `${LIKELIHOOD_LABELS[likelihoodCol]} likelihood × ${IMPACT_LABELS[impactRow]} impact (${count} risk${count === 1 ? '' : 's'})`
								return onSelectCell ? (
									<button
										key={key}
										type="button"
										onClick={() => onSelectCell({ likelihood: likelihoodCol, impact: impactRow })}
										aria-label={label}
										className={className}
									>
										{content}
									</button>
								) : (
									<div key={key} role="img" aria-label={label} className={className}>
										{content}
									</div>
								)
							})}
						</div>
					))}
				</div>
				{/* X-axis: likelihood 1..5 left-to-right */}
				<div className={cn('flex gap-1 mt-1', slide ? 'pl-36' : 'pl-16')}>
					{[1, 2, 3, 4, 5].map(l => (
						<div key={l} className={cn('flex-1 text-center text-gray-500', slide ? 'text-[14px]' : 'text-[10px]')}>
							<div className="text-gray-700 font-semibold">{l}</div>
							<div className="leading-tight">{LIKELIHOOD_LABELS[l]}</div>
						</div>
					))}
				</div>
				<div className={cn('text-center text-gray-500 uppercase tracking-wider mt-1', slide ? 'text-[14px]' : 'text-[10px]')}>Likelihood →</div>
			</div>
		</div>
	)
}

export default RiskHeatGrid
